import { Container, Stack } from "@mui/material";

import { enqueueSnackbar } from "notistack";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { Header } from "../../components";
import { ksefWebUrls } from "../../consts";
import { useStore } from "../../store";

import { CertificateMethodSteps } from "./CertificateMethodSteps";
import { CertificateUploadForm } from "./CertificateUploadForm";

export type CertificateMethodState = {
  certFile: File | undefined;
  keyFile: File | undefined;
  keyPassword: string;
  processing: boolean;
  steps: Partial<Record<number, boolean>>;
};

export const CertificateMethod: React.FC = () => {
  const { t } = useTranslation();
  const { applyCertificate, environment } = useStore();
  const [state, setState] = useState<CertificateMethodState>({
    certFile: undefined,
    keyFile: undefined,
    keyPassword: "",
    processing: false,
    steps: {},
  });

  const setStep = (step: number) => setState((prev) => ({ ...prev, steps: { ...prev.steps, [step]: true } }));

  const { certFile, keyFile, keyPassword, processing, steps } = state;

  const onMoveToKSef = () => {
    window.open(ksefWebUrls[environment], "_blank");
    setStep(1);
  };

  const onConfirm = async () => {
    if (!certFile || !keyFile || !keyPassword || keyPassword.trim() === "") return;

    setState((prev) => ({ ...prev, processing: true }));
    try {
      const success = await applyCertificate(certFile, keyFile, keyPassword);
      if (!success) {
        // Error is already handled in applyCertificate with snackbar
      }
    } catch (error) {
      console.error("Unexpected error in certificate method:", error);
      enqueueSnackbar(t("error.title"), { variant: "error" });
    } finally {
      setState((prev) => ({ ...prev, processing: false }));
    }
  };

  return (
    <Container maxWidth="sm">
      <Stack spacing={4}>
        <Header titleLoc="certificateMethod.title" onBackPath="/" />

        <Stack gap={2}>
          <CertificateMethodSteps steps={steps} onMoveToKSef={onMoveToKSef} onOpenUploadStep={() => setStep(2)} />
          {steps[2] && (
            <CertificateUploadForm
              disabled={processing}
              certFile={certFile}
              keyFile={keyFile}
              keyPassword={keyPassword}
              onCertFile={(file) => setState((prev) => ({ ...prev, certFile: file }))}
              onKeyFile={(file) => setState((prev) => ({ ...prev, keyFile: file }))}
              onKeyPasswordChange={(password) => setState((prev) => ({ ...prev, keyPassword: password }))}
              onConfirm={onConfirm}
            />
          )}
        </Stack>
      </Stack>
    </Container>
  );
};
